import React, { useState } from 'react'
import debounce from 'lodash.debounce';

import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    FormFeedback
} from 'reactstrap';

import { validation as validationFunctions } from '../utils';
import { checkEmailAvailability, sendEmailLink } from '../utils/firebase/auth';
import { maskEmail } from '../utils/helpers';
import { defaultFormData } from '../utils/constants/general';

function EmailLoginForm({ isModalContent = false, className = '', submitButtonClassName = '', showAlert = () => null, closeModal = () => null, showRegistrationForm = () => null }) {
  const [isLoading, setIsLoading] = useState(false);
  const [emailFormData, setEmailFormData] = useState({ ...defaultFormData.email });
  const idPrefix = isModalContent ? 'modal' : 'main';
  const emailInputId = `${idPrefix}-signin-email`;
  const emailFeedbackId = `${emailInputId}-feedback`;

  const {
    value: emailValue,
    isValid: isEmailValid,
    isTouched: isEmailTouched,
    message: emailErrorMessage
  } = emailFormData;

  const sendSignInEmail = debounce(async () => {
      let alertMessage = 'Something went wrong. Try again.';
  
      try {
        const { value: emailValue } = emailFormData;
  
        setIsLoading(true);

        const isEmailAvailable = await checkEmailAvailability(emailValue, true);

        if (!isEmailAvailable) {
          alertMessage = 'No account found for this email. Reserve a username first.';
        } else {
          await sendEmailLink(emailValue)
          alertMessage = `Sign-in link sent to ${maskEmail(emailValue)}.\nCheck your inbox and click the link to finish sign-in.`;
        }
      } catch ({ code = '', cause, message = '' }) {
        if (code === 'auth/email-already-in-use' || cause?.code === 'custom/email-already-in-use') {
          alertMessage = 'Email address is already in use. Try signing in with that email.';
        } else if (cause?.code === 'custom/username-already-in-use') {
          alertMessage = 'Username is already in use. Try signing in instead.';
        } else {
          alertMessage = message || 'There was a sign-in error. Try again soon. If it keeps happening, contact support@spitfacebattles.com';
        }
      } finally {
        setIsLoading(false);
        closeModal();
        showAlert(alertMessage, false);
      }
    }, 300);

    const handleValidation = (name, value) => {
      const validation = validationFunctions.validateEmail(value);
      return { ...emailFormData, ...validation, isTouched: emailFormData.isTouched };
    }; 
    
    const handleChange = ({ target: { name, value }}) => {
        const validation = handleValidation(name, value);
        setEmailFormData({
            ...emailFormData,
            ...validation,
            value
        });
    };

    const handleBlur = async ({ target: {name, value }}) => {
        let validation = handleValidation(name, value);
        let isAvailable = false;

        try {
          // setIsLoading(true);
          isAvailable = await checkEmailAvailability(emailValue);

        } catch ({ code = '', cause, message: errorMessage }) {
            let message = errorMessage;
            if (code === 'auth/email-already-in-use' || cause?.code === 'auth/email-already-in-use') {
              message = 'Email address is already in use. Try signing in.';
              validation = { ...validation, message, isValid: false };
            } else if (cause?.code === 'custom/username-already-in-use') {
              message = 'Username is already in use. Try signing in.';
              validation = { ...validation, message, isValid: false };
            }
        }finally {
            // setIsLoading(false);

            setEmailFormData({
              ...emailFormData,
              ...validation,
              isValid: !isAvailable,
              message: isAvailable ? 'No account found for this email. Reserve a username first.' : emailErrorMessage,
              isTouched: true
            });
        }
    };

    const handleSubmit = async event => {
        event.preventDefault();

        if (isEmailValid) {
          await sendSignInEmail();
          setEmailFormData({ ...defaultFormData.email });
        }
    };

  return (
    <Form 
        noValidate
        id="signin-form"
        onSubmit={handleSubmit}
        className={`form-shell d-flex flex-column justify-content-around gap-3 ${className}`}
    >
        <FormGroup floating className="no-fricken-bottom-margin permanent-marker-regular">
            <Input
                required
                type="email"
                name="email"
                id={emailInputId}
                placeholder="name@email.com"
                value={emailValue}
                invalid={isEmailTouched && !isEmailValid}
                aria-required="true"
                aria-invalid={isEmailTouched && !isEmailValid}
                aria-describedby={emailFeedbackId}
                tabIndex={2}
                onBlur={handleBlur}
                onChange={handleChange}
                className="input-tracked text-light text-light-emphasis bg-darker border-secondary-subtle rounded-2"
            />
            <Label for={emailInputId} className="text-secondary beta-form-label fw-semibold">Email Address</Label>
            <FormFeedback id={emailFeedbackId} aria-live="polite" className={`small mt-1 d-block ${isEmailTouched && !isEmailValid ? 'd-initial' : 'd-none'}`}>{emailErrorMessage}</FormFeedback>
        </FormGroup>

        {isModalContent === true && (
          <Button
            type="button"
            color="link"
            tabIndex={3}
            onClick={showRegistrationForm}
            className="align-self-center fw-medium text-secondary text-decoration-none anton-regular shadow-none"
          >
            Reserve Your Username
          </Button>
        )}

        <Button
            id={`${idPrefix}-signin-submit`}
            type='submit'
            color='primary'
            size="lg"
            tabIndex={3}
            aria-busy={isLoading}
            aria-live="polite"
            disabled={isLoading || !isEmailValid}
            className={`btn-cta align-self-center ${submitButtonClassName}`}
        >
            {isLoading ? 'Sending...' : 'Send Sign-In Link'}
        </Button>
    </Form>
  )
}

export default EmailLoginForm
